import { CommonModule, formatDate } from "@angular/common";
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import {
  FlyoutApplicationEnum,
  NotificationAudience,
  NotificationPlacement,
  PriorityEnum,
} from "src/app/shared/common/common.enum";
import { FlyoutService } from "src/app/shared/services/flyout.service";
import { MatSelectModule } from "@angular/material/select";

import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTimepickerModule } from "@angular/material/timepicker";
import {
  AngularEditorConfig,
  AngularEditorModule,
} from "@kolkov/angular-editor";
import { NotificationModel } from "../notification.model";
import { NotificationService } from "src/app/api/notification.service";
import { GenericResponse } from "src/app/shared/common/api-response.model";
import { GlobalServiceService } from "src/app/shared/services/global-service.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { endAfterStartValidator } from "./time.validatiors";
import { UtcToLocalPipe } from "src/app/shared/pipes/utc-to-local.pipe";

@Component({
  selector: "app-add-edit-notification",
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    FormsModule,
    MatSelectModule,
    MatDatepickerModule,
    MatTimepickerModule,
    AngularEditorModule,
    MatButtonModule,
  ],
  templateUrl: "./add-edit-notification.component.html",
  styleUrl: "./add-edit-notification.component.css",
})
export class AddEditNotificationComponent implements OnInit {
  @Output() onCancelClick = new EventEmitter<string>();
  appForm!: FormGroup;
  audienceType = NotificationAudience;
  notificationPlacement = NotificationPlacement;
  priorityEnum = PriorityEnum;
  @Input() notification!: NotificationModel;
  @Output() onDataRefresh: EventEmitter<boolean> = new EventEmitter<boolean>(
    false,
  );
  private snack = inject(MatSnackBar);

  // Minimal config; extend as needed
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    minHeight: "0",
    height: "auto",
    maxHeight: "auto",
    width: "auto",
    placeholder: "Type your content...",
    translate: "yes",
    enableToolbar: true,
    showToolbar: true,
    sanitize: true,
  };

  startMinDate = new Date(); // now
  max = new Date(new Date().setMonth(new Date().getMonth() + 3)); // +3 months

  endMinDate = this.startMinDate;

  minEndTime: Date | null = null;
  maxStartTime: Date | null = null;

  titleMaxLength = 150;
  messageMaxLength = 2000;
  isMobile = false;

  constructor(
    private fb: FormBuilder,
    private flyoutService: FlyoutService,
    private notificationService: NotificationService,
    private gbService: GlobalServiceService,
  ) {}

  ngOnInit(): void {
    this.isMobile = this.gbService.isMobile;
    this.appForm = this.fb.group(
      {
        title: [
          "",
          [Validators.required, Validators.maxLength(this.titleMaxLength)],
        ],
        audience: ["", Validators.required],
        placement: ["", Validators.required],
        startDate: ["", Validators.required],
        startTime: ["", Validators.required],
        endDate: ["", Validators.required],
        endTime: ["", [Validators.required]],
        htmlContent: [
          "",
          [Validators.required, Validators.maxLength(this.messageMaxLength)],
        ],
        priority: [PriorityEnum.Info, Validators.required],
      },
      { validators: [endAfterStartValidator] },
    );
    if (this.notification) {
      this.startMinDate = new Date(this.notification.StartAt);

      this.appForm.patchValue({
        title: this.notification.Title,
        audience: this.notification.Audience,
        placement: this.notification.Placement,
        startDate: new Date(this.notification.StartAt),
        startTime: this.toTimePickerDate(this.notification.StartAt),
        endDate: new Date(this.notification.EndAt),
        endTime: this.toTimePickerDate(this.notification.EndAt),
        htmlContent: this.notification.Message,
        priority: this.notification.Priority,
      });
      this.appForm.markAllAsTouched();
    }
    // this.appForm.updateValueAndValidity();
    this.subscribeValue();
  }

  private subscribeValue() {
    this.appForm.get("title")?.valueChanges.subscribe((value: string) => {
      if (value.length > 0) {
        this.flyoutService.setFlyoutHeader(this.gbService.transform(value));
      }
    });

    this.appForm.get("startDate")?.valueChanges.subscribe((value: string) => {
      if (value) {
        this.endMinDate = new Date(value);
        this.appForm.get("startTime")?.setValue(new Date(value));
      }
    });

    this.appForm.get("endDate")?.valueChanges.subscribe((end: string) => {
      if (end) {
        const endTime = this.addMinutes(
          new Date(this.appForm.get("startTime")?.value),
        );
        this.appForm.get("endTime")?.setValue(endTime);
      }
    });
  }

  addMinutes(date: Date, mins = 30): Date {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() + mins);
    return d;
  }

  onSubmit() {
    if (this.appForm.valid) {
      const data = this.appForm.value;
      const payload: NotificationModel = {
        Audience: data.audience,
        Title: data.title,
        Message: data.htmlContent,
        Placement: data.placement,
        StartAt: this.gbService.combineDateAndTimeToString(
          data.startDate,
          data.startTime,
        ),
        EndAt: this.gbService.combineDateAndTimeToString(
          data.endDate,
          data.endTime,
        ),
        CreatedBy: "",
        CreatedDate: "",
        IsActive: true,
        IsDeleted: false,
        ModifiedBy: "",
        ModifiedDate: "",
        NotificationId: this.notification?.NotificationId ?? 0,
        Priority: data.priority,
        InsertedBy: this.gbService.userData()?.["user-id"].toString() ?? "0",
      };
      this.notificationService.submitUserNotification(payload).subscribe({
        next: (res: GenericResponse<NotificationModel>) => {
          if (res.status && res.Result.NotificationId > 0) {
            this.snack.open("Notification Save Successfully", "Close", {
              duration: 8000,
            });
            this.onDataRefresh.emit(true);
            this.onCancel();
          } else {
            this.snack.open("Notification not saved", "Close", {
              duration: 8000,
            });
          }
        },
      });
    }
  }
  onCancel() {
    this.onCancelClick.emit(FlyoutApplicationEnum.notificationList);
  }

  parseSqlDateTime(sql: string) {
    const [datePart, timePart] = sql.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);

    const [hh, mm, ssMs] = timePart.split(":");
    const [ss, ms = "0"] = (ssMs || "").split(".");
    const hour = Number(hh);
    const minute = Number(mm);
    const second = Number(ss);
    const millisecond = Number(ms);

    return { year, month, day, hour, minute, second, millisecond };
  }

  /**
   * For typical time pickers: return a Date that carries the time component.
   * Uses today's date, but only the time is usually read by the control.
   */
  toTimePickerDate(sql: string): Date {
    const { hour, minute, second, millisecond } = this.parseSqlDateTime(sql);
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minute,
      second,
      millisecond,
    );
  }
}
